import { getPath, sanitizeField } from "core/helpers/utils";
import { useNotificationsContext } from "core/state/NotificationsContext/NotificationsContext";
import { useState, useMemo, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate } from "react-router-dom";
import {
  Notification,
  useArchiveNotificationFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { Container } from "shared/UI/LayoutComponents/LayoutComponents";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import NotificationsList from "./NotificationsList/NotificationsList";
import classNames from "classnames";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { RefreshIndicator, RefreshIndicatorType } from "UIPalette/RefreshIndicator/RefreshIndicator";

export type NotificationModuleProps = {
  hideComponent?: () => void;
  renderWithContainer?: boolean;
};

export function NotificationsModule({
  hideComponent,
  renderWithContainer = true,
}: NotificationModuleProps) {
  const navigate = useNavigate();
  const { isMobileApp } = useMobilePlatform();
  const largerScreen = useMatchMedia(ResponsiveBreakpoints.Desktop);
  const [listLength, setListLength] = useState(10);
  const [delayedLoading, setDelayedLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  let {
    notificationsErrors,
    refreshNotifications,
    notificationsList,
  } = useNotificationsContext();

  const { trigger } = useArchiveNotificationFunctionRequest();

  const visibleData = useMemo(
    () => notificationsList.slice(0, listLength),
    [notificationsList, listLength]
  );

  const refreshNotificationsListWithDelay = useCallback(async () => {
    setDelayedLoading(true);

    const MIN_DURATION = 1000;
    const start = Date.now();

    try {
      refreshNotifications();

      const elapsed = Date.now() - start;
      const remaining = Math.max(0, MIN_DURATION - elapsed);

      if (remaining > 0) {
        await new Promise((res) => setTimeout(res, remaining));
      }
    } finally {
      setDelayedLoading(false);
    }
  }, [refreshNotifications]);

  const handleArchive = useCallback(
    async (notification: Notification) => {
      if (notification.linkUrl) {
        try {
          const path = getPath(notification.linkUrl);
          navigate(path);
        } catch (e) {
          console.error("Error while navigating to the notification url", e)
        }
        hideComponent?.();
      }

      if (notification.isArchived) return;

      await trigger({
        query: { notificationId: sanitizeField(notification.id) },
      });

      refreshNotifications();
    },
    [navigate, trigger, refreshNotifications, hideComponent]
  );

  const Header = <PageHeader title="Notifications" hideForMobile hideBackForDesktop />;

  const handleScroll = (e: Event) => {
    const target = e.target as HTMLElement;

    setScrolled(target.scrollTop > 0);
  };

  return (
    <>
      {
        (!isMobileApp && !largerScreen && renderWithContainer) && <div><Container>{Header}</Container></div>
      }
      <div className="overflow-hidden">
        <div className={classNames("relative z-50 transition-shadow duration-500 h-0 w-full bg-white", { "shadow-strong": scrolled })}></div>

        <div data-testid="scrollTarget" id="scrollTarget" className={classNames("h-full overflow-scroll")}>
          <InfiniteScroll
            dataLength={visibleData.length}
            next={() => setListLength((prev) => prev + 5)}
            hasMore={notificationsList.length > visibleData.length}
            loader={null}
            refreshFunction={refreshNotificationsListWithDelay}
            pullDownToRefresh={!largerScreen}
            pullDownToRefreshThreshold={80}
            pullDownToRefreshContent={<RefreshIndicator type={RefreshIndicatorType.Pull} />}
            releaseToRefreshContent={<RefreshIndicator type={RefreshIndicatorType.Release} />}
            scrollableTarget="scrollTarget"
            onScroll={handleScroll}
          >
            <NotificationsList
              delayedLoading={delayedLoading}
              data={visibleData}
              visibleData={visibleData}
              handleArchive={handleArchive}
              newNotificationsErrors={notificationsErrors}
            />
          </InfiniteScroll>
        </div>
      </div>
    </>
  );
}
